import * as ss from 'simple-statistics';
import { z } from 'zod';
import type { Widget, WidgetIOSchema, DataType, Connection } from '../types/index.js';

// Types
export interface ValidationResult {
  valid: boolean;
  score: number; // 0-100
  issues: DataQualityIssue[];
  warnings: string[];
  suggestions: string[];
  metadata?: {
    totalRecords?: number;
    validRecords?: number;
    processingTime?: number;
    validators?: string[];
  };
}

export interface DataQualityIssue {
  field: string;
  type: 'missing' | 'outlier' | 'invalid' | 'stale' | 'inconsistent' | 'duplicate' | 'schema' | 'format';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  value?: any;
  expectedRange?: { min: any; max: any };
  location?: { row?: number; column?: string };
  autoFixable?: boolean;
  suggestedFix?: any;
}

export interface AnomalyResult {
  field: string;
  value: any;
  zscore?: number;
  percentile?: number;
  reason: string;
  confidence: number;
}

export interface ValidationRule {
  field: string;
  type: 'required' | 'range' | 'pattern' | 'custom';
  config: any;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export interface DataSchema {
  fields: Record<string, FieldSchema>;
  rules?: ValidationRule[];
}

export interface FieldSchema {
  type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object';
  required?: boolean;
  format?: string; // e.g., 'email', 'url', 'date-time', 'currency', 'percentage', 'ticker'
  constraints?: {
    min?: number;
    max?: number;
    pattern?: string;
    enum?: any[];
    precision?: number; // For decimal numbers
    scale?: number; // For decimal numbers
  };
  financialType?: 'price' | 'volume' | 'percentage' | 'ratio' | 'currency' | 'ticker';
}

// Financial data format validators
const financialValidators = {
  ticker: z.string().regex(/^[A-Z]{1,5}$/, 'Invalid ticker symbol format'),
  price: z.number().positive().finite(),
  volume: z.number().nonnegative().int(),
  percentage: z.number().min(-100).max(1000), // Allow for extreme gains
  ratio: z.number().finite(),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Invalid currency code'),
};

// Statistical thresholds for financial data
const FINANCIAL_THRESHOLDS = {
  priceChangePercent: { daily: 20, weekly: 50, monthly: 100 },
  volumeSpike: 5, // 5x average volume
  peRatio: { min: -1000, max: 1000, typical: { min: 10, max: 30 } },
  marketCap: { min: 0, max: 10e12 }, // Up to 10 trillion
};

export class DataQualityValidator {
  private customValidators = new Map<string, (data: any) => ValidationResult>();
  private zodSchemas = new Map<string, z.ZodSchema>();
  
  /**
   * Validate financial data quality
   */
  validateFinancialData(data: any, schema?: DataSchema): ValidationResult {
    const issues: DataQualityIssue[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];
    
    // Check if data exists
    if (!data || (Array.isArray(data) && data.length === 0)) {
      issues.push({
        field: 'data',
        type: 'missing',
        severity: 'critical',
        description: 'No data provided for validation'
      });
      return { valid: false, score: 0, issues, warnings, suggestions };
    }
    
    // Validate against schema if provided
    if (schema) {
      this.validateAgainstSchema(data, schema, issues);
    }
    
    // Perform financial-specific validations
    if (Array.isArray(data)) {
      this.validateFinancialTimeSeries(data, issues, warnings, suggestions);
    } else if (typeof data === 'object') {
      this.validateFinancialMetrics(data, issues, warnings);
    }
    
    // Calculate quality score
    const score = this.calculateQualityScore(issues);
    
    return {
      valid: issues.filter(i => i.severity === 'critical').length === 0,
      score,
      issues,
      warnings,
      suggestions
    };
  }

  /**
   * Detect anomalies in data
   */
  detectAnomalies(data: any[], field?: string): AnomalyResult[] {
    const anomalies: AnomalyResult[] = [];
    
    if (!Array.isArray(data) || data.length < 3) {
      return anomalies;
    }
    
    // Extract numeric values
    const values = field 
      ? data.map(item => item[field]).filter(v => typeof v === 'number')
      : data.filter(v => typeof v === 'number');
    
    if (values.length < 3) return anomalies;
    
    // Calculate statistics
    const mean = ss.mean(values);
    const stdDev = ss.standardDeviation(values);
    const q1 = ss.quantile(values, 0.25);
    const q3 = ss.quantile(values, 0.75);
    const iqr = q3 - q1;
    
    // Detect outliers using multiple methods
    values.forEach((value) => {
      const anomalyReasons: string[] = [];
      let confidence = 0;
      
      // Z-score method (for normal distributions)
      const zscore = stdDev > 0 ? (value - mean) / stdDev : 0;
      if (Math.abs(zscore) > 3) {
        anomalyReasons.push(`Z-score: ${zscore.toFixed(2)}`);
        confidence += 0.3;
      }
      
      // IQR method (for skewed distributions)
      const lowerBound = q1 - 1.5 * iqr;
      const upperBound = q3 + 1.5 * iqr;
      if (value < lowerBound || value > upperBound) {
        anomalyReasons.push('Outside IQR bounds');
        confidence += 0.3;
      }
      
      // Percentile method
      const percentile = ss.quantileRank(values, value);
      if (percentile < 0.01 || percentile > 0.99) {
        anomalyReasons.push(`Extreme percentile: ${(percentile * 100).toFixed(1)}%`);
        confidence += 0.4;
      }
      
      if (anomalyReasons.length > 0) {
        anomalies.push({
          field: field || 'value',
          value,
          zscore: Math.abs(zscore),
          percentile: percentile * 100,
          reason: anomalyReasons.join(', '),
          confidence: Math.min(confidence, 1)
        });
      }
    });
    
    return anomalies;
  }

  /**
   * Validate data completeness
   */
  validateCompleteness(data: any[], requiredFields: string[]): ValidationResult {
    const issues: DataQualityIssue[] = [];
    let missingCount = 0;
    let totalChecks = 0;
    
    data.forEach((item, index) => {
      requiredFields.forEach(field => {
        totalChecks++;
        if (item[field] === null || item[field] === undefined || item[field] === '') {
          missingCount++;
          issues.push({
            field,
            type: 'missing',
            severity: 'medium',
            description: `Missing value at row ${index + 1}`,
            location: { row: index + 1, column: field }
          });
        }
      });
    });
    
    const completenessScore = totalChecks > 0 
      ? ((totalChecks - missingCount) / totalChecks) * 100 
      : 100;
    
    return {
      valid: completenessScore >= 80, // 80% threshold
      score: completenessScore,
      issues,
      warnings: completenessScore < 90 
        ? [`Data completeness is ${completenessScore.toFixed(1)}%`] 
        : [],
      suggestions: completenessScore < 100 
        ? ['Consider filling missing values or removing incomplete records'] 
        : []
    };
  }

  /**
   * Validate time series data consistency
   */
  validateTimeSeriesConsistency(
    data: Array<{ date: string | Date; value: number }>,
    expectedInterval?: 'daily' | 'weekly' | 'monthly'
  ): ValidationResult {
    const issues: DataQualityIssue[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];
    
    if (data.length < 2) {
      return { valid: true, score: 100, issues, warnings, suggestions };
    }
    
    // Sort by date
    const sorted = [...data].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    // Check for duplicates
    const dateSet = new Set<string>();
    sorted.forEach((item, index) => {
      const dateStr = new Date(item.date).toISOString();
      if (dateSet.has(dateStr)) {
        issues.push({
          field: 'date',
          type: 'duplicate',
          severity: 'high',
          description: `Duplicate date found: ${dateStr}`,
          value: item.date,
          location: { row: index + 1 }
        });
      }
      dateSet.add(dateStr);
    });
    
    // Check interval consistency
    if (expectedInterval) {
      const expectedMs = this.getIntervalMilliseconds(expectedInterval);
      let inconsistentIntervals = 0;
      
      for (let i = 1; i < sorted.length; i++) {
        const interval = new Date(sorted[i].date).getTime() - 
                        new Date(sorted[i - 1].date).getTime();
        
        // Allow 10% tolerance
        if (Math.abs(interval - expectedMs) > expectedMs * 0.1) {
          inconsistentIntervals++;
        }
      }
      
      if (inconsistentIntervals > sorted.length * 0.1) {
        warnings.push(`Time series intervals are inconsistent with expected ${expectedInterval} frequency`);
        suggestions.push('Consider resampling the data to ensure consistent intervals');
      }
    }
    
    // Check for gaps
    const gaps = this.findTimeSeriesGaps(sorted);
    gaps.forEach(gap => {
      warnings.push(`Gap detected: ${gap.days} days between ${gap.start} and ${gap.end}`);
    });
    
    const score = this.calculateQualityScore(issues);
    
    return {
      valid: issues.filter(i => i.severity === 'critical' || i.severity === 'high').length === 0,
      score,
      issues,
      warnings,
      suggestions
    };
  }

  /**
   * Register custom validator
   */
  registerCustomValidator(name: string, validator: (data: any) => ValidationResult): void {
    this.customValidators.set(name, validator);
  }

  /**
   * Register Zod schema for validation
   */
  registerZodSchema(name: string, schema: z.ZodSchema): void {
    this.zodSchemas.set(name, schema);
  }

  /**
   * Validate data against a Zod schema
   */
  validateWithZod(data: any, schemaName: string): ValidationResult {
    const schema = this.zodSchemas.get(schemaName);
    if (!schema) {
      return {
        valid: false,
        score: 0,
        issues: [{
          field: 'schema',
          type: 'invalid',
          severity: 'critical',
          description: `Schema '${schemaName}' not found`
        }],
        warnings: [],
        suggestions: []
      };
    }

    try {
      schema.parse(data);
      return { valid: true, score: 100, issues: [], warnings: [], suggestions: [] };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const issues: DataQualityIssue[] = error.errors.map(err => ({
          field: err.path.join('.'),
          type: 'schema' as const,
          severity: 'high' as const,
          description: err.message,
          value: err.code === 'invalid_type' ? undefined : data
        }));
        
        return {
          valid: false,
          score: Math.max(0, 100 - issues.length * 20),
          issues,
          warnings: [],
          suggestions: ['Review schema requirements and fix validation errors']
        };
      }
      throw error;
    }
  }

  /**
   * Run custom validator
   */
  runCustomValidator(name: string, data: any): ValidationResult | null {
    const validator = this.customValidators.get(name);
    if (!validator) return null;
    
    return validator(data);
  }

  /**
   * Validate widget data flow
   */
  validateWidgetDataFlow(widget: Widget, inputData: Record<string, any>): ValidationResult {
    const issues: DataQualityIssue[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];
    const startTime = Date.now();

    // Validate each input against its schema
    widget.inputs.forEach(input => {
      const value = inputData[input.id];
      
      if (input.schema) {
        this.validateAgainstIOSchema(value, input.schema, input.name, issues);
      }

      // Check for missing required inputs
      if (!input.connected && value === undefined) {
        warnings.push(`Input '${input.name}' is not connected and has no value`);
      }
    });

    // Calculate score and return result
    const score = this.calculateQualityScore(issues);
    const processingTime = Date.now() - startTime;

    return {
      valid: issues.filter(i => i.severity === 'critical' || i.severity === 'high').length === 0,
      score,
      issues,
      warnings,
      suggestions,
      metadata: {
        processingTime,
        validators: ['schema', 'type', 'required']
      }
    };
  }

  /**
   * Validate connection compatibility
   */
  validateConnection(connection: Connection, sourceWidget: Widget, targetWidget: Widget): ValidationResult {
    const issues: DataQualityIssue[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    const sourceOutput = sourceWidget.outputs.find(o => o.id === connection.source.outputId);
    const targetInput = targetWidget.inputs.find(i => i.id === connection.target.inputId);

    if (!sourceOutput || !targetInput) {
      issues.push({
        field: 'connection',
        type: 'invalid',
        severity: 'critical',
        description: 'Invalid connection: source output or target input not found'
      });
      return { valid: false, score: 0, issues, warnings, suggestions };
    }

    // Type compatibility check
    if (sourceOutput.type !== targetInput.type && targetInput.type !== 'any') {
      if (!this.canConvertType(sourceOutput.type, targetInput.type)) {
        issues.push({
          field: 'type',
          type: 'invalid',
          severity: 'high',
          description: `Type mismatch: ${sourceOutput.type} cannot be converted to ${targetInput.type}`,
          autoFixable: true,
          suggestedFix: { transformer: this.getTypeTransformer(sourceOutput.type, targetInput.type) }
        });
      } else {
        warnings.push(`Type conversion needed: ${sourceOutput.type} to ${targetInput.type}`);
        suggestions.push('Consider adding a transformer to the connection');
      }
    }

    // Schema compatibility check
    if (sourceOutput.schema && targetInput.schema) {
      this.validateSchemaCompatibility(sourceOutput.schema, targetInput.schema, issues, warnings);
    }

    const score = this.calculateQualityScore(issues);
    return { valid: score >= 60, score, issues, warnings, suggestions };
  }

  /**
   * Statistical analysis utilities
   */
  calculateStatistics(data: number[]): {
    mean: number;
    median: number;
    stdDev: number;
    variance: number;
    skewness: number;
    kurtosis: number;
    percentiles: { p25: number; p50: number; p75: number; p90: number; p95: number };
  } {
    if (data.length === 0) {
      throw new Error('Cannot calculate statistics on empty data');
    }

    const sorted = [...data].sort((a, b) => a - b);
    
    return {
      mean: ss.mean(data),
      median: ss.median(sorted),
      stdDev: ss.standardDeviation(data),
      variance: ss.variance(data),
      skewness: data.length >= 3 ? ss.sampleSkewness(data) : 0,
      kurtosis: data.length >= 4 ? ss.sampleKurtosis(data) : 0,
      percentiles: {
        p25: ss.quantile(sorted, 0.25),
        p50: ss.quantile(sorted, 0.50),
        p75: ss.quantile(sorted, 0.75),
        p90: ss.quantile(sorted, 0.90),
        p95: ss.quantile(sorted, 0.95)
      }
    };
  }

  /**
   * Validate specific financial metrics
   */
  validateFinancialMetric(value: any, metricType: string): ValidationResult {
    const issues: DataQualityIssue[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    try {
      switch (metricType) {
        case 'ticker':
          financialValidators.ticker.parse(value);
          break;
        
        case 'price':
          financialValidators.price.parse(value);
          // Additional price checks
          if (value > 100000) {
            warnings.push('Unusually high price - verify data accuracy');
          }
          break;
        
        case 'volume':
          financialValidators.volume.parse(value);
          break;
        
        case 'percentage':
          financialValidators.percentage.parse(value);
          if (Math.abs(value) > 50) {
            warnings.push(`Large percentage change: ${value}%`);
          }
          break;
        
        case 'peRatio':
          if (value < FINANCIAL_THRESHOLDS.peRatio.min || value > FINANCIAL_THRESHOLDS.peRatio.max) {
            issues.push({
              field: 'peRatio',
              type: 'outlier',
              severity: 'medium',
              description: 'P/E ratio outside reasonable bounds',
              value,
              expectedRange: FINANCIAL_THRESHOLDS.peRatio
            });
          } else if (value < FINANCIAL_THRESHOLDS.peRatio.typical.min || 
                     value > FINANCIAL_THRESHOLDS.peRatio.typical.max) {
            warnings.push(`P/E ratio ${value} is outside typical range`);
          }
          break;
        
        default:
          warnings.push(`No specific validator for metric type: ${metricType}`);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach(err => {
          issues.push({
            field: metricType,
            type: 'format',
            severity: 'high',
            description: err.message,
            value
          });
        });
      }
    }

    const score = this.calculateQualityScore(issues);
    return { valid: issues.length === 0, score, issues, warnings, suggestions };
  }

  // Private methods

  private validateAgainstSchema(data: any, schema: DataSchema, issues: DataQualityIssue[]): void {
    // Validate fields
    Object.entries(schema.fields).forEach(([fieldName, fieldSchema]) => {
      const value = data[fieldName];
      
      // Check required fields
      if (fieldSchema.required && (value === null || value === undefined)) {
        issues.push({
          field: fieldName,
          type: 'missing',
          severity: 'high',
          description: `Required field '${fieldName}' is missing`
        });
        return;
      }
      
      // Skip validation if field is optional and not present
      if (value === null || value === undefined) return;
      
      // Type validation
      if (!this.validateType(value, fieldSchema.type)) {
        issues.push({
          field: fieldName,
          type: 'invalid',
          severity: 'high',
          description: `Expected ${fieldSchema.type} but got ${typeof value}`,
          value
        });
      }
      
      // Constraint validation
      if (fieldSchema.constraints) {
        this.validateConstraints(value, fieldSchema.constraints, fieldName, issues);
      }
    });
    
    // Apply custom rules
    if (schema.rules) {
      schema.rules.forEach(rule => {
        this.applyValidationRule(data, rule, issues);
      });
    }
  }

  private validateType(value: any, expectedType: string): boolean {
    switch (expectedType) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number' && !isNaN(value);
      case 'boolean':
        return typeof value === 'boolean';
      case 'date':
        return value instanceof Date || !isNaN(Date.parse(value));
      case 'array':
        return Array.isArray(value);
      case 'object':
        return typeof value === 'object' && !Array.isArray(value) && value !== null;
      default:
        return true;
    }
  }

  private validateConstraints(
    value: any, 
    constraints: any, 
    fieldName: string, 
    issues: DataQualityIssue[]
  ): void {
    if (constraints.min !== undefined && value < constraints.min) {
      issues.push({
        field: fieldName,
        type: 'invalid',
        severity: 'medium',
        description: `Value ${value} is below minimum ${constraints.min}`,
        value,
        expectedRange: { min: constraints.min, max: constraints.max }
      });
    }
    
    if (constraints.max !== undefined && value > constraints.max) {
      issues.push({
        field: fieldName,
        type: 'invalid',
        severity: 'medium',
        description: `Value ${value} exceeds maximum ${constraints.max}`,
        value,
        expectedRange: { min: constraints.min, max: constraints.max }
      });
    }
    
    if (constraints.pattern && typeof value === 'string') {
      const regex = new RegExp(constraints.pattern);
      if (!regex.test(value)) {
        issues.push({
          field: fieldName,
          type: 'invalid',
          severity: 'medium',
          description: `Value does not match required pattern`,
          value
        });
      }
    }
    
    if (constraints.enum && !constraints.enum.includes(value)) {
      issues.push({
        field: fieldName,
        type: 'invalid',
        severity: 'medium',
        description: `Value must be one of: ${constraints.enum.join(', ')}`,
        value
      });
    }
  }

  private applyValidationRule(data: any, rule: ValidationRule, issues: DataQualityIssue[]): void {
    const severity = rule.severity || 'medium';
    
    switch (rule.type) {
      case 'required':
        if (!data[rule.field]) {
          issues.push({
            field: rule.field,
            type: 'missing',
            severity,
            description: `Required field '${rule.field}' is missing or empty`
          });
        }
        break;
        
      case 'range':
        const value = data[rule.field];
        if (typeof value === 'number') {
          if (rule.config.min !== undefined && value < rule.config.min) {
            issues.push({
              field: rule.field,
              type: 'invalid',
              severity,
              description: `Value ${value} is below minimum ${rule.config.min}`,
              value,
              expectedRange: rule.config
            });
          }
          if (rule.config.max !== undefined && value > rule.config.max) {
            issues.push({
              field: rule.field,
              type: 'invalid',
              severity,
              description: `Value ${value} exceeds maximum ${rule.config.max}`,
              value,
              expectedRange: rule.config
            });
          }
        }
        break;
        
      case 'custom':
        if (rule.config.validator && !rule.config.validator(data[rule.field], data)) {
          issues.push({
            field: rule.field,
            type: 'invalid',
            severity,
            description: rule.config.message || `Custom validation failed for ${rule.field}`,
            value: data[rule.field]
          });
        }
        break;
    }
  }

  private validateFinancialTimeSeries(
    data: any[], 
    issues: DataQualityIssue[], 
    warnings: string[], 
    suggestions: string[]
  ): void {
    // Check for minimum data points
    if (data.length < 20) {
      warnings.push('Limited data points for reliable analysis');
      suggestions.push('Consider gathering more historical data for better insights');
    }
    
    // Detect anomalies
    const anomalies = this.detectAnomalies(data);
    anomalies.forEach(anomaly => {
      if (anomaly.confidence > 0.7) {
        issues.push({
          field: anomaly.field,
          type: 'outlier',
          severity: 'medium',
          description: `Potential outlier detected: ${anomaly.reason}`,
          value: anomaly.value
        });
      }
    });
    
    // Check for data staleness
    if (data.length > 0 && data[0].date) {
      const latestDate = new Date(Math.max(...data.map(d => new Date(d.date).getTime())));
      const daysSinceUpdate = (Date.now() - latestDate.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysSinceUpdate > 7) {
        warnings.push(`Data is ${Math.floor(daysSinceUpdate)} days old`);
        suggestions.push('Update data for current market conditions');
      }
    }
  }

  private validateFinancialMetrics(
    data: any, 
    issues: DataQualityIssue[], 
    warnings: string[]
  ): void {
    // Check for negative values where unexpected
    const nonNegativeFields = ['price', 'volume', 'marketCap', 'revenue'];
    nonNegativeFields.forEach(field => {
      if (data[field] !== undefined && data[field] < 0) {
        issues.push({
          field,
          type: 'invalid',
          severity: 'high',
          description: `${field} should not be negative`,
          value: data[field]
        });
      }
    });
    
    // Check for reasonable ranges
    if (data.peRatio !== undefined) {
      if (data.peRatio < 0) {
        warnings.push('Negative P/E ratio indicates company is not profitable');
      } else if (data.peRatio > 100) {
        warnings.push('Very high P/E ratio may indicate overvaluation');
      }
    }
    
    // Check for data consistency
    if (data.high !== undefined && data.low !== undefined && data.close !== undefined) {
      if (data.close > data.high || data.close < data.low) {
        issues.push({
          field: 'close',
          type: 'inconsistent',
          severity: 'high',
          description: 'Closing price outside of daily range',
          value: data.close,
          expectedRange: { min: data.low, max: data.high }
        });
      }
    }
  }

  private calculateQualityScore(issues: DataQualityIssue[]): number {
    if (issues.length === 0) return 100;
    
    let score = 100;
    const severityWeights = {
      low: 5,
      medium: 10,
      high: 20,
      critical: 40
    };
    
    issues.forEach(issue => {
      score -= severityWeights[issue.severity];
    });
    
    return Math.max(0, score);
  }

  private getIntervalMilliseconds(interval: 'daily' | 'weekly' | 'monthly'): number {
    switch (interval) {
      case 'daily':
        return 24 * 60 * 60 * 1000;
      case 'weekly':
        return 7 * 24 * 60 * 60 * 1000;
      case 'monthly':
        return 30 * 24 * 60 * 60 * 1000; // Approximate
      default:
        return 24 * 60 * 60 * 1000;
    }
  }

  private findTimeSeriesGaps(data: Array<{ date: string | Date; value: number }>): Array<{
    start: string;
    end: string;
    days: number;
  }> {
    const gaps: Array<{ start: string; end: string; days: number }> = [];
    const threshold = 7; // Days
    
    for (let i = 1; i < data.length; i++) {
      const prevDate = new Date(data[i - 1].date);
      const currDate = new Date(data[i].date);
      const daysDiff = (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysDiff > threshold) {
        gaps.push({
          start: prevDate.toISOString().split('T')[0],
          end: currDate.toISOString().split('T')[0],
          days: Math.floor(daysDiff)
        });
      }
    }
    
    return gaps;
  }

  private validateAgainstIOSchema(value: any, schema: WidgetIOSchema, fieldName: string, issues: DataQualityIssue[]): void {
    // Type validation
    if (!this.validateIOType(value, schema.type)) {
      issues.push({
        field: fieldName,
        type: 'invalid',
        severity: 'high',
        description: `Expected ${schema.type} but got ${typeof value}`,
        value
      });
      return;
    }

    // Format validation
    if (schema.format && value !== null && value !== undefined) {
      this.validateFormat(value, schema.format, fieldName, issues);
    }

    // Constraint validation
    if (schema.constraints) {
      this.validateConstraints(value, schema.constraints, fieldName, issues);
    }

    // Nested validation for objects and arrays
    if (schema.type === 'object' && schema.properties && typeof value === 'object') {
      Object.entries(schema.properties).forEach(([key, subSchema]) => {
        if (value[key] !== undefined) {
          this.validateAgainstIOSchema(value[key], subSchema, `${fieldName}.${key}`, issues);
        }
      });
    } else if (schema.type === 'array' && schema.items && Array.isArray(value)) {
      value.forEach((item, index) => {
        this.validateAgainstIOSchema(item, schema.items!, `${fieldName}[${index}]`, issues);
      });
    }
  }

  private validateIOType(value: any, expectedType: DataType): boolean {
    if (expectedType === 'any') return true;
    
    switch (expectedType) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number' && !isNaN(value);
      case 'boolean':
        return typeof value === 'boolean';
      case 'array':
        return Array.isArray(value);
      case 'object':
        return typeof value === 'object' && !Array.isArray(value) && value !== null;
      default:
        return true;
    }
  }

  private validateFormat(value: any, format: string, fieldName: string, issues: DataQualityIssue[]): void {
    let isValid = true;
    let message = '';

    switch (format) {
      case 'stock-symbol':
      case 'ticker':
        isValid = typeof value === 'string' && /^[A-Z]{1,5}$/.test(value);
        message = 'Stock symbol must be 1-5 uppercase letters';
        break;
      
      case 'percentage':
        isValid = typeof value === 'number' && value >= -100 && value <= 1000;
        message = 'Percentage must be between -100 and 1000';
        break;
      
      case 'currency':
        isValid = typeof value === 'string' && /^[A-Z]{3}$/.test(value);
        message = 'Currency code must be 3 uppercase letters';
        break;
      
      case 'date':
      case 'date-time':
        isValid = !isNaN(Date.parse(value));
        message = 'Invalid date format';
        break;
      
      case 'email':
        isValid = typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        message = 'Invalid email format';
        break;
      
      case 'url':
        try {
          new URL(value);
          isValid = true;
        } catch {
          isValid = false;
        }
        message = 'Invalid URL format';
        break;
    }

    if (!isValid) {
      issues.push({
        field: fieldName,
        type: 'format',
        severity: 'medium',
        description: message,
        value
      });
    }
  }

  private canConvertType(sourceType: DataType, targetType: DataType): boolean {
    // Define type conversion compatibility matrix
    const conversionMatrix: Record<DataType, DataType[]> = {
      'string': ['number', 'boolean'],
      'number': ['string', 'boolean'],
      'boolean': ['string', 'number'],
      'array': [],
      'object': ['string'], // JSON stringification
      'any': ['string', 'number', 'boolean', 'array', 'object']
    };

    return conversionMatrix[sourceType]?.includes(targetType) || false;
  }

  private getTypeTransformer(sourceType: DataType, targetType: DataType): ((value: any) => any) | null {
    const key = `${sourceType}->${targetType}`;
    
    const transformers: Record<string, (value: any) => any> = {
      'string->number': (v) => parseFloat(v),
      'string->boolean': (v) => v.toLowerCase() === 'true',
      'number->string': (v) => String(v),
      'number->boolean': (v) => v !== 0,
      'boolean->string': (v) => String(v),
      'boolean->number': (v) => v ? 1 : 0,
      'object->string': (v) => JSON.stringify(v)
    };

    return transformers[key] || null;
  }

  private validateSchemaCompatibility(
    sourceSchema: WidgetIOSchema,
    targetSchema: WidgetIOSchema,
    issues: DataQualityIssue[],
    warnings: string[]
  ): void {
    // Check format compatibility
    if (sourceSchema.format && targetSchema.format && sourceSchema.format !== targetSchema.format) {
      warnings.push(`Format mismatch: source expects '${sourceSchema.format}' but target expects '${targetSchema.format}'`);
    }

    // Check constraint compatibility
    if (sourceSchema.constraints && targetSchema.constraints) {
      const sourceMin = sourceSchema.constraints.min;
      const sourceMax = sourceSchema.constraints.max;
      const targetMin = targetSchema.constraints.min;
      const targetMax = targetSchema.constraints.max;

      if (sourceMin !== undefined && targetMax !== undefined && sourceMin > targetMax) {
        issues.push({
          field: 'constraints',
          type: 'inconsistent',
          severity: 'high',
          description: `Source minimum (${sourceMin}) exceeds target maximum (${targetMax})`
        });
      }

      if (sourceMax !== undefined && targetMin !== undefined && sourceMax < targetMin) {
        issues.push({
          field: 'constraints',
          type: 'inconsistent',
          severity: 'high',
          description: `Source maximum (${sourceMax}) is less than target minimum (${targetMin})`
        });
      }
    }
  }
}

// Export singleton instance
export const dataQualityValidator = new DataQualityValidator();